import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';
import { Sparkles, Upload, Loader2 } from 'lucide-react';
import axios from 'axios';
import { emit, EVT } from '../../services/dataSyncBus';
import { track, trackFirstTime } from '../../services/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RecordExpenseModal = ({ isOpen, onClose, onSuccess, token }) => {
  const [properties, setProperties] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [aiCatLoading, setAiCatLoading] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    property_id: '',
    room_id: '',
    category: 'maintenance',
    amount: '',
    description: '',
    expense_date: new Date().toISOString().split('T')[0],
    payment_method: 'cash',
    receipt_url: ''
  });

  const handleAIOCR = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOcrLoading(true);
    setOcrResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/ai/ocr/expense-bill`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const d = res.data;
      setOcrResult(d);
      setFormData(prev => ({
        ...prev,
        amount: d.amount ? String(d.amount) : prev.amount,
        category: d.category || prev.category,
        description: d.vendor ? `${d.vendor}${d.description ? ' - ' + d.description : ''}` : (d.description || prev.description),
        expense_date: d.expense_date || prev.expense_date,
      }));
      toast({
        title: "✨ AI Bill Scanned",
        description: `Auto-filled: ₹${d.amount || 0} • ${d.category} • ${Math.round((d.confidence || 0) * 100)}% confidence`
      });
    } catch (error) {
      toast({
        title: "OCR Failed",
        description: error.response?.data?.detail || "Could not extract bill data",
        variant: "destructive"
      });
    } finally {
      setOcrLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAICategorize = async () => {
    if (!formData.description?.trim()) {
      toast({ title: "Add a description first", variant: "destructive" });
      return;
    }
    setAiCatLoading(true);
    try {
      const res = await axios.post(`${API}/ai/categorize/expense`, { description: formData.description }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData(prev => ({ ...prev, category: res.data.category }));
      toast({
        title: "✨ AI Categorized",
        description: `Detected: ${res.data.category} (${Math.round(res.data.confidence * 100)}% confidence)`
      });
    } catch (error) {
      toast({
        title: "Categorization failed",
        description: error.response?.data?.detail || "Try again",
        variant: "destructive"
      });
    } finally {
      setAiCatLoading(false);
    }
  };

  const fetchProperties = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  }, [token]);

  const handlePropertyChange = async (propertyId) => {
    setFormData({ ...formData, property_id: propertyId, room_id: '' });
    setRooms([]);
    
    if (propertyId) {
      try {
        const response = await axios.get(`${API}/properties/${propertyId}/rooms`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRooms(response.data);
      } catch (error) {
        console.error('Error fetching rooms:', error);
        setRooms([]);
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchProperties();
    }
  }, [isOpen, fetchProperties]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/money/expenses`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      emit(EVT.EXPENSE, { action: 'created', amount: formData.amount });
      track('expense_logged', {
        category: formData.category,
        amount: Number(formData.amount) || 0,
        has_photo: !!formData.bill_photo,
      });
      trackFirstTime('first_expense_logged', { category: formData.category });

      toast({
        title: "Expense Recorded!",
        description: `₹${formData.amount} expense recorded successfully`
      });

      onSuccess();
      onClose();
      resetForm();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to record expense",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  function resetForm() {
    setFormData({
      property_id: '',
      room_id: '',
      category: 'maintenance',
      amount: '',
      description: '',
      expense_date: new Date().toISOString().split('T')[0],
      payment_method: 'cash',
      receipt_url: ''
    });
    setRooms([]);
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Record Expense</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="expense-form">
          {/* AI OCR Upload */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="text-purple-600" size={18} />
              <span className="text-sm font-semibold text-purple-900">AI Bill Scanner</span>
              <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">NEW</span>
            </div>
            <p className="text-xs text-gray-600 mb-3">Upload a bill/receipt image — AI will auto-fill amount, category, vendor & date.</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleAIOCR}
              className="hidden"
              data-testid="expense-ocr-file-input"
            />
            <Button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={ocrLoading}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
              data-testid="expense-ocr-upload-btn"
            >
              {ocrLoading ? (
                <><Loader2 className="animate-spin mr-2" size={16} /> Scanning...</>
              ) : (
                <><Upload className="mr-2" size={16} /> Scan Bill with AI</>
              )}
            </Button>
            {ocrResult && (
              <div className="mt-3 p-2 bg-white rounded border border-purple-100 text-xs">
                <p className="text-gray-700"><strong>Extracted:</strong> ₹{ocrResult.amount} • {ocrResult.vendor || 'Vendor'} • {ocrResult.category} • {ocrResult.expense_date || 'No date'}</p>
                <p className="text-purple-600 mt-1">Confidence: {Math.round((ocrResult.confidence || 0) * 100)}%</p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Property *</Label>
              <select
                data-testid="expense-property-select"
                value={formData.property_id}
                onChange={(e) => handlePropertyChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select Property</option>
                {properties.map(prop => (
                  <option key={prop.id} value={prop.id}>{prop.name}</option>
                ))}
              </select>
            </div>

            {/* Room selection - optional */}
            {formData.property_id && (
              <div className="col-span-2">
                <Label>Room (Optional)</Label>
                <select
                  value={formData.room_id}
                  onChange={(e) => setFormData({...formData, room_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">General (Not specific to any room)</option>
                  {rooms.map(room => (
                    <option key={room.id} value={room.id}>
                      Room {room.room_number} - {room.room_type}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty for property-wide expenses
                </p>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between">
                <Label>Category *</Label>
                <button
                  type="button"
                  onClick={handleAICategorize}
                  disabled={aiCatLoading || !formData.description?.trim()}
                  className="text-xs flex items-center gap-1 text-purple-600 hover:text-purple-700 disabled:opacity-40 font-medium"
                  data-testid="expense-ai-categorize-btn"
                  title="AI-categorize based on description"
                >
                  {aiCatLoading ? <Loader2 className="animate-spin" size={12} /> : <Sparkles size={12} />}
                  AI Categorize
                </button>
              </div>
              <select
                data-testid="expense-category-select"
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="maintenance">🔧 Maintenance</option>
                <option value="electricity">💡 Electricity</option>
                <option value="water">💧 Water</option>
                <option value="staff">👷 Staff Salary</option>
                <option value="repairs">🛠️ Repairs</option>
                <option value="internet">📶 Internet</option>
                <option value="gas">🔥 Gas</option>
                <option value="cleaning">🧹 Cleaning</option>
                <option value="security">🔒 Security</option>
                <option value="furniture">🪑 Furniture</option>
                <option value="supplies">📦 Supplies</option>
                <option value="marketing">📣 Marketing</option>
                <option value="legal">⚖️ Legal</option>
                <option value="taxes">🧾 Taxes</option>
                <option value="others">📋 Others</option>
              </select>
            </div>

            <div>
              <Label>Amount (₹) *</Label>
              <Input
                data-testid="expense-amount-input"
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                placeholder="1000"
                required
              />
            </div>

            <div className="col-span-2">
              <Label>Description *</Label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Describe the expense..."
                required
              />
            </div>

            <div>
              <Label>Expense Date *</Label>
              <Input
                type="date"
                value={formData.expense_date}
                onChange={(e) => setFormData({...formData, expense_date: e.target.value})}
                required
              />
            </div>

            <div>
              <Label>Payment Method *</Label>
              <select
                value={formData.payment_method}
                onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="card">Card</option>
                <option value="bank_transfer">Bank Transfer</option>
              </select>
            </div>

            <div className="col-span-2">
              <Label>Receipt URL (optional)</Label>
              <Input
                type="url"
                value={formData.receipt_url}
                onChange={(e) => setFormData({...formData, receipt_url: e.target.value})}
                placeholder="https://..."
              />
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <Button data-testid="expense-submit-btn" type="submit" className="flex-1 bg-red-600 hover:bg-red-700" disabled={loading}>
              {loading ? 'Recording...' : 'Record Expense'}
            </Button>
            <Button data-testid="expense-cancel-btn" type="button" onClick={onClose} variant="outline" className="flex-1">
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RecordExpenseModal;
